import { useEffect, useRef, useState } from 'react'
import { Controller, type Control } from 'react-hook-form'
import type { ResidentDefaultFormValues } from '../../types'
import { formatNoteWhen } from '@/lib/utils/noteFormatting'
import {
  formatSecurityDepositReasonsForForm,
  SECURITY_DEPOSIT_REASON_OPTIONS,
} from '../../utils/securityDepositChangeReason'

function selectedReasons(value: unknown): string[] {
  return formatSecurityDepositReasonsForForm(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

function SecurityDepositReasonDropdown({
  value,
  disabled,
  hasError,
  defaultOpen = false,
  onToggle,
}: {
  value: string[]
  disabled?: boolean
  hasError?: boolean
  defaultOpen?: boolean
  onToggle: (reason: string) => void
}) {
  const [open, setOpen] = useState(defaultOpen)
  const rootRef = useRef<HTMLDivElement>(null)
  const label = value.length ? value.join(', ') : 'Select reason(s)'

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  return (
    <div ref={rootRef} className="relative mt-1">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        className={`flex h-10 w-full items-center justify-between rounded-lg border bg-white px-3 text-left text-sm font-medium text-slate-700 ${
          hasError ? 'border-red-500' : 'border-slate-200'
        }`}
      >
        <span>{label}</span>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 shadow-sm">
          {SECURITY_DEPOSIT_REASON_OPTIONS.map((reason) => (
            <label key={reason} className="flex cursor-pointer items-center gap-2 px-2 py-1.5">
              <input
                type="checkbox"
                checked={value.includes(reason)}
                disabled={disabled}
                onChange={() => onToggle(reason)}
              />
              <span>{reason}</span>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function SecurityDepositChangeReasonField({
  control,
  disabled,
  defaultOpen = false,
}: {
  control: Control<ResidentDefaultFormValues>
  disabled?: boolean
  defaultOpen?: boolean
}) {
  return (
    <Controller
      control={control}
      name="security_deposit_change_reasons"
      render={({ field, fieldState }) => {
        const value = selectedReasons(field.value)
        const toggle = (reason: string) => {
          if (disabled) return
          const next = value.includes(reason)
            ? value.filter((item) => item !== reason)
            : [...value, reason]
          field.onChange(next.join(', '))
        }

        return (
          <>
            <SecurityDepositReasonDropdown
              value={value}
              disabled={disabled}
              hasError={Boolean(fieldState.error)}
              defaultOpen={defaultOpen}
              onToggle={toggle}
            />
            {fieldState.error?.message ? (
              <p className="mt-1 text-xs text-red-600">{fieldState.error.message}</p>
            ) : null}
          </>
        )
      }}
    />
  )
}

export function SecurityDepositReasonLabel({ value }: { value: unknown }) {
  const reasons = selectedReasons(value)
  if (!reasons.length) return null

  return (
    <p className="text-sm leading-snug text-slate-600">
      <span className="font-semibold text-slate-700"></span> {reasons.join(', ')}
    </p>
  )
}

export function SecurityDepositAuditLine({
  residentDefault,
}: {
  residentDefault?: Record<string, unknown> | null
}) {
  const updatedAt = residentDefault?.security_deposit_updated_at
  const updatedBy = String(residentDefault?.security_deposit_updated_by_name ?? '').trim()

  if (!updatedAt) return null

  return (
    <p className="mt-1.5 text-xs leading-snug text-slate-500">
      Edited By {updatedBy || 'System'} - {formatNoteWhen(updatedAt)}
    </p>
  )
}
